// Module 10.6 — adaptive quality (§12). THE single central per-tier knob table for the whole viz.
// setQuality drops RICHNESS only across MANY independent axes (user requirement: "consider many layers
// beyond resolution and Hz"): render resolution, MSAA, FXAA, fog, 3D-tile SSE, particles, arc density,
// flow cadence, static arcs, wall outlines, the energy-flow edge cap, and the energy glyph cap. It
// NEVER touches a data channel (served fractions, statuses, SoC, flow magnitudes are identical across
// tiers — the app governor steps fidelity, not correctness). Degradation order (best→worst):
// ultra → highplus → high → mediumplus → medium → low. The v1.1.1 intermediate tiers let the v2
// governor step in smaller increments with less visible pop as it probes for the highest quality the
// device can hold above the 28fps floor. BOTH adapter.applyQuality (resolution/MSAA/FXAA/fog/SSE/
// FLOW_CAP) and qualityFlags (particles/arc density/cadence/static/outline) read THIS ONE table — DRY.
import type { QualityTier } from "../contract";
import type { QualityFlags } from "./spec";

/**
 * All fidelity knobs the quality system drives, per tier. `renderScale` is a multiplier on
 * devicePixelRatio (capped where shown); the adapter computes `resolutionScale = renderScaleFor(dpr,tier)/dpr`.
 * `flowCap` / `glyphCap` bound how many energy edges / glyphs render at once.
 */
export interface TierKnobs extends QualityFlags {
  /** Render-buffer scale vs CSS pixels (the dominant adaptive lever; independent of device dpr). */
  renderScale: (dpr: number) => number;
  /** MSAA sample count (WebGL2; 1 = off). */
  msaaSamples: 1 | 2 | 4 | 8;
  /** FXAA post-process on/off (cheaper than MSAA, masks its blur). */
  fxaa: boolean;
  /** Bloom post-process — REMOVED entirely (2026-07-06 pt.2, always false at every tier); kept as a
   *  typed field so a future re-introduction attempt is a one-line, reviewable diff, not a re-add of the
   *  whole knob. See adapter.ts applyQuality, which forces bloom.enabled=false unconditionally regardless
   *  of this value — this flag no longer has any effect on the rendered scene. */
  bloom: boolean;
  /** Globe fog on/off (also drives fog-culling — keep on except at the lowest tier). */
  fog: boolean;
  /** 3D Tiles `maximumScreenSpaceError` (lower = sharper geometry, more tiles). */
  tileSse: number;
  /** Hard cap on the number of energy-flow edges that get marching glyphs/arrows (Infinity = all). */
  flowCap: number;
  /** Hard cap on the energy glyph billboard count (saves draw calls + GPU fill on weaker devices). */
  glyphCap: number;
}

/**
 * The central tier→knobs table. Order MUST match `TIER_ORDER` (best→worst) in the governor + env-signals.
 * Numerical values are tuned so each step is a SMALL, mostly-monotonic fidelity change (no big jumps).
 */
export const TIER_KNOBS: Record<QualityTier, TierKnobs> = {
  ultra: {
    renderScale: (dpr) => Math.min(dpr, 2), // true native, capped 2× (a 4K panel renders real 4K)
    msaaSamples: 8,
    fxaa: true,
    bloom: false, // REMOVED (2026-07-06 pt.2): bloom caused sustained user-reported flashing/oscillating
    // brightness even after the brightness/contrast retune — root cause was architectural (governor
    // tier-flip toggles bloom on/off near the high/mediumplus FPS boundary with near-zero probe-side
    // hysteresis, PLUS bloom reacting every tick to legitimate per-tick emission changes in the P14
    // loading-danger bands). Disabled UNCONDITIONALLY at every tier per explicit user authorization to
    // remove the effect rather than re-tune it again. See adapter.ts applyQuality (forces bloom off
    // regardless of this flag, as defense-in-depth) and build log for the removal.
    fog: true,
    tileSse: 8, // sharpest
    flowCap: Number.POSITIVE_INFINITY,
    glyphCap: 4000,
    arcDensityFrac: 1.0,
    flowCadence: 1,
    arcsStatic: false,
    wallsOutline: false,
  },
  highplus: {
    renderScale: (dpr) => Math.min(dpr, 1.75),
    msaaSamples: 4,
    fxaa: true,
    bloom: false, // REMOVED — see ultra tier's comment above
    fog: true,
    tileSse: 10,
    flowCap: Number.POSITIVE_INFINITY,
    glyphCap: 4000,
    arcDensityFrac: 1.0,
    flowCadence: 1,
    arcsStatic: false,
    wallsOutline: false,
  },
  high: {
    renderScale: (dpr) => Math.min(dpr, 1.5), // crisp; caps the supersample cost
    msaaSamples: 4,
    fxaa: true,
    bloom: false, // REMOVED — see ultra tier's comment above (this was the exact tier boundary the
    // governor's probe/rollback cycled across, the primary oscillation trigger)
    fog: true,
    tileSse: 12,
    flowCap: Number.POSITIVE_INFINITY,
    glyphCap: 4000,
    arcDensityFrac: 1.0,
    flowCadence: 1,
    arcsStatic: false,
    wallsOutline: false,
  },
  mediumplus: {
    renderScale: () => 1.25,
    msaaSamples: 4,
    fxaa: true,
    bloom: false, // bloom REMOVED entirely (2026-07-06 pt.2) — see ultra tier's comment; was already off here
    fog: true,
    tileSse: 14,
    flowCap: 15000,
    glyphCap: 3000,
    arcDensityFrac: 0.75,
    flowCadence: 2,
    arcsStatic: false,
    wallsOutline: false,
  },
  medium: {
    renderScale: () => 1.0, // 1 CSS px (dpr ignored — the lighter default look)
    msaaSamples: 2,
    fxaa: false,
    bloom: false,
    fog: true,
    tileSse: 16, // 3D Tiles default
    flowCap: 10000,
    glyphCap: 2500,
    arcDensityFrac: 0.5,
    flowCadence: 2,
    arcsStatic: false,
    wallsOutline: false,
  },
  low: {
    renderScale: () => 0.6, // downscaled — the survival tier for weak/integrated GPUs
    msaaSamples: 1,
    fxaa: false,
    bloom: false,
    fog: false,
    tileSse: 32, // coarsest
    flowCap: 2000,
    glyphCap: 1500,
    arcDensityFrac: 0.25,
    flowCadence: 4,
    arcsStatic: true,
    wallsOutline: true,
  },
};

/** The richness-only subset (arc density/cadence/static/outline). Phase 0.3: `particles` removed. */
export function qualityFlags(tier: QualityTier): QualityFlags {
  const k = TIER_KNOBS[tier];
  return {
    arcDensityFrac: k.arcDensityFrac,
    flowCadence: k.flowCadence,
    arcsStatic: k.arcsStatic,
    wallsOutline: k.wallsOutline,
  };
}

/** The full knob set for a tier (consumed by adapter.applyQuality for the render-side knobs). */
export function tierKnobs(tier: QualityTier): TierKnobs {
  return TIER_KNOBS[tier];
}
